import { nextTick, watch } from "vue"
import model from "../model";
import { getPaths, navigateTo, waitForViewLoad } from "./view-navigation-management";
import { addQueryParams, getQueryParam, getScript, isObject } from "../utils/utils";
import { updatePopState } from "./pop-state-management";
import type { CategoryID } from "../types/category-id-types";
import { showMobileSideNav } from "./ui-management";
import { replaceState } from "../utils/browser-history-helper";

type RouteData = string | number | (number | string)[];

const SB_PREFIX = 'sb__';

let t = 0;
let savedPath: string | undefined;

export default {
    init() {
        let dispose = watch(() => model.view, async view =>{
            if(view != 'sport-betting') return;
            await initManagement();
            await nextTick();
            dispose();
        }, {immediate: true});
    },
}

const alternateCultures: {culture: string, replacementCulture: string}[] = [
    {
        culture: 'es-MX',
        replacementCulture: 'es-ES',
    },
    {
        culture: 'es-CO',
        replacementCulture: 'es-ES',
    },
];

function updateCulture(){
    let culture = alternateCultures.find(x => x.culture == model.locale);
    model.sportBetting.options.culture = culture?.replacementCulture || model.locale;
}

async function initManagement(){
    await waitForViewLoad('sport-betting');

    const url = model.clientConfig.scripts?.sportBetting?.url;
    if(!url) return;

    model.sportBetting.options = Object.assign(JSON.parse(JSON.stringify(model.clientConfig.scripts?.sportBetting?.options || {})));

    updateCulture();

    await getScript(url, false);
    model.sportBettingScriptLoaded = true;

    watch(() => model.uToken, () => reinitialize(), {immediate: true});
    watch(() => model.activeModal, () => reinitialize());
    watch(() => model.lang, () => {
        if(!window.altenarWSDK || model.view != 'sport-betting') return;

        updateCulture();
        window.altenarWSDK.set({
            culture: model.sportBetting.options.culture,
        });

        savedPath = undefined;
    });

    model.on('Navigation', () => {
        if(model.view != 'sport-betting') savedPath = undefined;
        reinitialize();
    });

    model.on('SportBettingRoute', e => {
        const detail = (e as CustomEvent).detail;
        if(!window.altenarWSDK || !model.sportBetting.initialized) return;

        let data = detail.data;
        if(!data) return;

        if(model.view != 'sport-betting'){
            navigateTo('sport-betting', {ignorePopState: true});
            return;
        }

        window.altenarWSDK.set(dissectPageParams(data));

        showMobileSideNav(false, true);
    });

    function destroySB(){
        if(model.elemRefs.sportBetting){
            model.elemRefs.sportBetting.innerHTML = '';
        }

        if(model.sportBetting.instance){
            model.sportBetting.instance.remove();
            model.sportBetting.instance = null;

            if(model.sportBetting.options.token){
                window.altenarWSDK?.set({
                    token: '',
                });
                delete model.sportBetting.options.token;
            }
        }
    }

    function reinitialize(){
        if(!window.altenarWSDK || !model.sportBetting.options) return;

        destroySB();

        clearTimeout(t);
        t = setTimeout(() => {
            if(!window.altenarWSDK) return;
            if(model.view != 'sport-betting' || model.activeModal) return;

            const params = getPageParams();

            if(model.uToken) model.sportBetting.options.token = model.uToken;

            if(!model.sportBetting.initialized){
                window.altenarWSDK.init(model.sportBetting.options);
                model.sportBetting.initialized = true;
            }
            else if(typeof model.sportBetting.options.token === 'string' && model.sportBetting.options.token) {
                window.altenarWSDK.set({
                    token: model.sportBetting.options.token,
                });
            }

            model.sportBetting.instance = window.altenarWSDK.addSportsBook({
                props: {
                    ...params,
                    routerType: 'memory',
                    onSignInButtonClick: () => navigateTo('login'),
                    onRouteChange: (data: Record<string, RouteData>) => {
                        console.log(data);

                        const paths = getPaths();

                        const page = data.page;
                        if(!page) return;
                    
                        let sbData = SB_PREFIX;

                        let keys = Object.keys(data);

                        for(let key in data){
                            let val = data[key];

                            sbData += key + '-';

                            if(Array.isArray(val)){
                                sbData += 'array-' + val.join('-');
                            }
                            else{
                                sbData += val;
                            }

                            if(keys[keys.length - 1] != key) sbData += '_';
                        }

                        let url: string[] = [];
                        if(model.testWebHostname) url.push(model.testWebHostname);
                        if(model.supportedLangs.length > 0) url.push(model.lang);
                        url.push(paths[0] || '');
                        url.push(sbData);

                        let urlStr = '/' + url.join('/');
                        savedPath = urlStr;

                        updatePopState('sport-betting', 'sport-betting', urlStr);
                    },
                },
                container: model.elemRefs.sportBetting,
            });
        }, 100);
    }
}

function getPageParams(){
    if(savedPath && history.state?.pathname) {
        history.state.pathname = savedPath;
        replaceState(history.state, document.title, savedPath + location.search);
    }

    const paths = getPaths();
    if(paths.length == 1){
        const firstParam = paths[0];
        const secondParam = SB_PREFIX + 'page-overview';

        if(model.supportedLangs.length > 0) paths.unshift(model.lang);
        if(model.testWebHostname) paths.unshift(model.testWebHostname);

        paths.push(secondParam);
        if(history.state) history.state.pathname = '/' + [firstParam, secondParam].join('/');
        replaceState(history.state, document.title, '/' + paths.join('/') + location.search);

        return { page: 'overview' };
    }

    let secondPath = paths[1];
    if(!secondPath) return { page: 'overview' };

    if(!secondPath.includes(SB_PREFIX)) {
        navigateTo('error', {ignorePopState: true});
        return;
    }

    return dissectPageParams(secondPath);
}

function dissectPageParams(data: string){
    data = data.substring(data.indexOf(SB_PREFIX) + SB_PREFIX.length);
    let params: Record<string, RouteData> = {};

    let arrData = data.split('_');
    for(let data of arrData){
        let dataSplit = data.split('-') as (number | string)[];
        let key = dataSplit.shift();
        let val = dataSplit.shift() as RouteData;

        if(!key) continue;

        if(val == 'array'){
            dataSplit.forEach((item, index) => {
                if(!isNaN(Number(item))) dataSplit[index] = Number(item);
            });
            val = dataSplit;
        }
        else if(!isNaN(Number(val))) val = Number(val);

        params[key] = val;
    }

    return params;
} 